import React, { useState, useEffect, useCallback } from 'react';
import { getAdminNutzer, getAdminStatistiken, setzeRolle, loescheAdminNutzer, setzeAdminAbo, loescheAdminAbo } from '../api';
import './AdminPanel.css';

const PAGE_SIZE = 50;

// ── Hilfsfunktionen ──────────────────────────────────────────

function datumsAnzeige(isoStr) {
  if (!isoStr) return '—';
  try {
    return new Date(isoStr).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return isoStr;
  }
}

function datumKurz(isoStr) {
  if (!isoStr) return null;
  try {
    return new Date(isoStr).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch {
    return null;
  }
}

function initialien(displayName, email) {
  if (displayName && displayName.trim()) {
    const teile = displayName.trim().split(' ');
    return (teile[0][0] + (teile[1]?.[0] || '')).toUpperCase();
  }
  return (email?.[0] || '?').toUpperCase();
}

// ── Skeleton ─────────────────────────────────────────────────

function SkeletonReihen({ anzahl = 8, spalten = 4 }) {
  return Array.from({ length: anzahl }).map((_, i) => (
    <tr key={i} className="ap-skeleton-row">
      {Array.from({ length: spalten }).map((__, j) => (
        <td key={j}><div className="ap-skeleton-cell" style={{ width: j === 0 ? '60%' : '80px' }} /></td>
      ))}
    </tr>
  ));
}

// ── Statistik-Tab ─────────────────────────────────────────────

function StatistikTab() {
  const [stats, setStats] = useState(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');

  useEffect(() => {
    getAdminStatistiken()
      .then(res => setStats(res.data))
      .catch(() => setFehler('Statistiken konnten nicht geladen werden.'))
      .finally(() => setLaden(false));
  }, []);

  if (laden) {
    return (
      <div className="ap-stats-grid">
        {[1, 2, 3].map(i => (
          <div key={i} className="ap-stat-card">
            <div className="ap-skeleton-cell" style={{ height: 44, width: '60%', marginBottom: 8 }} />
            <div className="ap-skeleton-cell" style={{ height: 14, width: '40%' }} />
          </div>
        ))}
      </div>
    );
  }

  if (fehler) {
    return <div className="ap-meldung ap-meldung--fehler">{fehler}</div>;
  }

  const gesamt = stats?.gesamt ?? 0;
  const admins = stats?.nach_rolle?.admin ?? 0;
  const users = stats?.nach_rolle?.user ?? 0;

  return (
    <div className="ap-stats-grid">
      <div className="ap-stat-card ap-stat-card--primary">
        <div className="ap-stat-icon">👥</div>
        <div className="ap-stat-zahl">{gesamt}</div>
        <div className="ap-stat-label">Nutzer gesamt</div>
      </div>
      <div className="ap-stat-card">
        <div className="ap-stat-icon">🛡️</div>
        <div className="ap-stat-zahl">{admins}</div>
        <div className="ap-stat-label">Admins</div>
      </div>
      <div className="ap-stat-card">
        <div className="ap-stat-icon">🙋</div>
        <div className="ap-stat-zahl">{users}</div>
        <div className="ap-stat-label">Reguläre Nutzer</div>
      </div>
    </div>
  );
}

// ── Nutzer-Tabelle-Tab ────────────────────────────────────────

function NutzerTabelleTab({ eigeneUid }) {
  const [nutzer, setNutzer] = useState([]);
  const [gesamt, setGesamt] = useState(0);
  const [seite, setSeite] = useState(0);
  const [laden, setLaden] = useState(true);
  const [rolleAendernLaden, setRolleAendernLaden] = useState(null);
  const [meldung, setMeldung] = useState(null);
  const [loeschenUid, setLoeschenUid] = useState(null);
  const [loeschenLaeuft, setLoeschenLaeuft] = useState(false);

  const ladeNutzer = useCallback((offset) => {
    setLaden(true);
    getAdminNutzer(PAGE_SIZE, offset)
      .then(res => {
        setNutzer(res.data.nutzer);
        setGesamt(res.data.gesamt);
      })
      .catch(() => setMeldung({ typ: 'fehler', text: 'Nutzerliste konnte nicht geladen werden.' }))
      .finally(() => setLaden(false));
  }, []);

  useEffect(() => {
    ladeNutzer(seite * PAGE_SIZE);
  }, [seite, ladeNutzer]);

  const onNutzerLoeschen = async (uid) => {
    setLoeschenLaeuft(true);
    try {
      await loescheAdminNutzer(uid);
      setNutzer(prev => prev.filter(n => n.uid !== uid));
      setGesamt(prev => prev - 1);
      setLoeschenUid(null);
      setMeldung({ typ: 'erfolg', text: 'Nutzer wurde vollständig gelöscht.' });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Nutzer konnte nicht gelöscht werden.';
      setMeldung({ typ: 'fehler', text: detail });
      setLoeschenUid(null);
    } finally {
      setLoeschenLaeuft(false);
    }
  };

  const onRolleAendern = async (uid, neueRolle) => {
    setRolleAendernLaden(uid);
    setMeldung(null);
    try {
      await setzeRolle(uid, neueRolle);
      setNutzer(prev => prev.map(n => n.uid === uid ? { ...n, role: neueRolle } : n));
      setMeldung({
        typ: 'erfolg',
        text: `Rolle geändert zu "${neueRolle}". Wirksam beim nächsten Token-Refresh (max. 1h).`,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Rolle konnte nicht geändert werden.';
      setMeldung({ typ: 'fehler', text: detail });
    } finally {
      setRolleAendernLaden(null);
    }
  };

  const vonIndex = seite * PAGE_SIZE + 1;
  const bisIndex = Math.min((seite + 1) * PAGE_SIZE, gesamt);

  return (
    <>
      {meldung && (
        <div className={`ap-meldung ap-meldung--${meldung.typ}`}>
          {meldung.typ === 'erfolg' ? '✓' : '✕'} {meldung.text}
        </div>
      )}

      <div className="ap-sektion-header">
        <span className="ap-sektion-titel">Alle Nutzer</span>
        <span className="ap-sektion-anzahl">{gesamt} Einträge</span>
      </div>

      <div className="ap-tabelle-wrapper">
        <table className="ap-tabelle">
          <thead>
            <tr>
              <th>Nutzer</th>
              <th>Rolle</th>
              <th>Letzte Anmeldung</th>
              <th>Rolle ändern</th>
              <th>Löschen</th>
            </tr>
          </thead>
          <tbody>
            {laden ? (
              <SkeletonReihen anzahl={8} spalten={5} />
            ) : nutzer.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <div className="ap-leer">
                    <div className="ap-leer-icon">📋</div>
                    <div className="ap-leer-text">Keine Nutzer gefunden.</div>
                  </div>
                </td>
              </tr>
            ) : (
              nutzer.map(n => (
                <tr key={n.uid}>
                  <td>
                    <div className="ap-nutzer-zelle">
                      <div className="ap-avatar">
                        {n.photoURL
                          ? <img src={n.photoURL} alt="" />
                          : initialien(n.displayName, n.email)
                        }
                      </div>
                      <div>
                        <div className="ap-nutzer-name">
                          {n.displayName || '—'}
                          {n.uid === eigeneUid && (
                            <span style={{ fontSize: 11, color: 'var(--txt-muted)', marginLeft: 6 }}>(Du)</span>
                          )}
                        </div>
                        <div className="ap-nutzer-email">{n.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`ap-rolle-badge ap-rolle-badge--${n.role || 'user'}`}>
                      {n.role || 'user'}
                    </span>
                  </td>
                  <td>
                    <span className="ap-datum">{datumsAnzeige(n.lastLogin)}</span>
                  </td>
                  <td>
                    <select
                      className="ap-rolle-select"
                      value={n.role || 'user'}
                      disabled={rolleAendernLaden === n.uid || n.uid === eigeneUid}
                      onChange={e => onRolleAendern(n.uid, e.target.value)}
                      title={n.uid === eigeneUid ? 'Eigene Rolle kann nicht geändert werden' : 'Rolle ändern'}
                    >
                      <option value="user">user</option>
                      <option value="admin">admin</option>
                    </select>
                  </td>
                  <td>
                    {n.uid === eigeneUid ? (
                      <span style={{ fontSize: 12, color: 'var(--txt-muted)' }}>—</span>
                    ) : loeschenUid === n.uid ? (
                      <div className="ap-loeschen-confirm">
                        <span className="ap-loeschen-frage">Wirklich?</span>
                        <button className="ap-loeschen-ja" onClick={() => onNutzerLoeschen(n.uid)} disabled={loeschenLaeuft}>
                          {loeschenLaeuft ? '…' : 'Ja'}
                        </button>
                        <button className="ap-loeschen-nein" onClick={() => setLoeschenUid(null)} disabled={loeschenLaeuft}>
                          Nein
                        </button>
                      </div>
                    ) : (
                      <button
                        className="ap-aktion-btn ap-aktion-btn--delete"
                        onClick={() => setLoeschenUid(n.uid)}
                        title="Nutzer löschen"
                      >
                        ✕
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {gesamt > PAGE_SIZE && (
          <div className="ap-pagination">
            <span className="ap-pagination-info">Zeige {vonIndex}–{bisIndex} von {gesamt}</span>
            <div className="ap-pagination-btns">
              <button className="ap-pagination-btn" disabled={seite === 0} onClick={() => setSeite(s => s - 1)}>
                ← Zurück
              </button>
              <button className="ap-pagination-btn" disabled={bisIndex >= gesamt} onClick={() => setSeite(s => s + 1)}>
                Weiter →
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ── Nutzungs-Tab ──────────────────────────────────────────────

function _proAktiv(n) {
  const sub = n.subscription;
  if (!sub || !sub.active) return false;
  try { return new Date(sub.expires_at) > new Date(); } catch { return false; }
}

// ── Pro-Toggle-Zelle ──────────────────────────────────────────

function ProToggleZelle({ n, onAboGesetzt, onAboGeloescht }) {
  const istPro = _proAktiv(n);
  const [bestaetigen, setBestaetigen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState(false);

  const onVerleihen = async () => {
    setLaeuft(true);
    setFehler(false);
    try {
      const res = await setzeAdminAbo(n.uid);
      onAboGesetzt(n.uid, res.data.subscription);
    } catch {
      setFehler(true);
    } finally {
      setLaeuft(false);
    }
  };

  const onEntziehen = async () => {
    setLaeuft(true);
    setFehler(false);
    try {
      const res = await loescheAdminAbo(n.uid);
      onAboGeloescht(n.uid, res.data.subscription);
      setBestaetigen(false);
    } catch {
      setFehler(true);
    } finally {
      setLaeuft(false);
    }
  };

  if (fehler) return <span className="ap-quota-fehler">✕ Fehler</span>;

  if (istPro && bestaetigen) {
    return (
      <div className="ap-loeschen-confirm">
        <span className="ap-loeschen-frage">Entziehen?</span>
        <button className="ap-loeschen-ja" onClick={onEntziehen} disabled={laeuft}>
          {laeuft ? '…' : 'Ja'}
        </button>
        <button className="ap-loeschen-nein" onClick={() => setBestaetigen(false)} disabled={laeuft}>
          Nein
        </button>
      </div>
    );
  }

  return (
    <button
      className={`ap-aktion-btn ${istPro ? 'ap-aktion-btn--pro-aktiv' : 'ap-aktion-btn--pro'}`}
      onClick={istPro ? () => setBestaetigen(true) : onVerleihen}
      disabled={laeuft}
      title={istPro ? 'Pro entziehen' : 'CVFlow Pro verleihen'}
    >
      {laeuft ? '…' : istPro ? '★ Pro' : '☆ Pro'}
    </button>
  );
}

function NutzungTab() {
  const [nutzer, setNutzer] = useState([]);
  const [gesamt, setGesamt] = useState(0);
  const [seite, setSeite] = useState(0);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');
  const ladeNutzer = useCallback((offset) => {
    setLaden(true);
    getAdminNutzer(PAGE_SIZE, offset)
      .then(res => {
        setNutzer(res.data.nutzer);
        setGesamt(res.data.gesamt);
      })
      .catch(() => setFehler('Nutzerliste konnte nicht geladen werden.'))
      .finally(() => setLaden(false));
  }, []);

  useEffect(() => {
    ladeNutzer(seite * PAGE_SIZE);
  }, [seite, ladeNutzer]);

  const onAboGesetzt = (uid, sub) => {
    setNutzer(prev => prev.map(n => n.uid === uid ? { ...n, subscription: sub } : n));
  };

  const onAboGeloescht = (uid, sub) => {
    setNutzer(prev => prev.map(n => n.uid === uid ? { ...n, subscription: sub } : n));
  };

  const vonIndex = seite * PAGE_SIZE + 1;
  const bisIndex = Math.min((seite + 1) * PAGE_SIZE, gesamt);

  if (fehler) return <div className="ap-meldung ap-meldung--fehler">{fehler}</div>;

  return (
    <>
      <div className="ap-sektion-header">
        <span className="ap-sektion-titel">Abonnements</span>
        <span className="ap-sektion-anzahl">{gesamt} Nutzer</span>
      </div>

      <div className="ap-tabelle-wrapper">
        <table className="ap-tabelle ap-tabelle--nutzung">
          <thead>
            <tr>
              <th>Nutzer</th>
              <th>Abo-Status</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {laden ? (
              <SkeletonReihen anzahl={8} spalten={3} />
            ) : nutzer.length === 0 ? (
              <tr>
                <td colSpan={3}>
                  <div className="ap-leer">
                    <div className="ap-leer-icon">📋</div>
                    <div className="ap-leer-text">Keine Nutzer gefunden.</div>
                  </div>
                </td>
              </tr>
            ) : (
              nutzer.map(n => (
                <tr key={n.uid}>
                  <td>
                    <div className="ap-nutzer-zelle">
                      <div className="ap-avatar">
                        {n.photoURL
                          ? <img src={n.photoURL} alt="" />
                          : initialien(n.displayName, n.email)
                        }
                      </div>
                      <div>
                        <div className="ap-nutzer-name">{n.displayName || '—'}</div>
                        <div className="ap-nutzer-email">{n.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {_proAktiv(n)
                      ? <span className="ap-pro-badge">PRO</span>
                      : <span className="ap-datum">—</span>
                    }
                  </td>
                  <td>
                    <div className="ap-aktionen-zelle">
                      <ProToggleZelle n={n} onAboGesetzt={onAboGesetzt} onAboGeloescht={onAboGeloescht} />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {gesamt > PAGE_SIZE && (
          <div className="ap-pagination">
            <span className="ap-pagination-info">Zeige {vonIndex}–{bisIndex} von {gesamt}</span>
            <div className="ap-pagination-btns">
              <button className="ap-pagination-btn" disabled={seite === 0} onClick={() => setSeite(s => s - 1)}>
                ← Zurück
              </button>
              <button className="ap-pagination-btn" disabled={bisIndex >= gesamt} onClick={() => setSeite(s => s + 1)}>
                Weiter →
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ── Haupt-Komponente ──────────────────────────────────────────

export default function AdminPanel({ nutzer }) {
  const [aktiveTab, setAktiveTab] = useState('uebersicht');

  const tabs = [
    { id: 'uebersicht', label: 'Übersicht' },
    { id: 'nutzer', label: 'Nutzerverwaltung' },
    { id: 'nutzung', label: 'Nutzung & Pakete' },
  ];

  return (
    <div className="ap-container">
      <div className="ap-header">
        <div className="ap-header-top">
          <div>
            <h1 className="ap-header-title">Admin-Panel</h1>
            <p className="ap-header-subtitle">Nutzerverwaltung & Rollen</p>
          </div>
          <div className="ap-header-badge">
            🛡️ Administrator
          </div>
        </div>

        <div className="ap-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`ap-tab-btn ${aktiveTab === tab.id ? 'aktiv' : ''}`}
              onClick={() => setAktiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="ap-content">
        {aktiveTab === 'uebersicht' && <StatistikTab />}
        {aktiveTab === 'nutzer' && <NutzerTabelleTab eigeneUid={nutzer?.uid} />}
        {aktiveTab === 'nutzung' && <NutzungTab />}
      </div>
    </div>
  );
}
